/**
 * SettingsPanel — top-level Settings modal. Sections land in here as
 * features need them; phase 1 ships Export / Import / Restore.
 *
 * Section pattern (reusable for future Ports, TouchOSC sections):
 *   - header (title + intro paragraph)
 *   - primary action (when there is one)
 *   - secondary actions
 *   - optional danger zone at bottom for destructive actions
 *
 * The three sections share visual structure but read as distinct
 * concerns: Export = "save out", Import = "bring in", Restore =
 * "roll back". Restore is destructive and uses the danger styling.
 */

import { createSignal, createMemo, onCleanup, onMount, For, Show } from 'solid-js';
import { dockPref, setDockPref } from '../navigate/dock.ts';
import { reloadOpens, setReloadOpens } from '../navigate/resume.ts';
import { openConnect, barPosition, setBarPosition, editTriggersEnabled, setEditTriggersEnabled, snapEnabled, setSnapEnabled, controlGap, setControlGap, CONTROL_GAP_MAX, openTrafficMonitor, settingsFocusSection, clearSettingsFocusSection } from '../settings/store.ts';
import { stageDim, setStageDim, STAGE_DIM_MIN, STAGE_DIM_MAX } from '../performance/store.ts';
import { blackBoxVerdict, blackBoxHistorySummary } from '../diag/blackBox.ts';
import { buildDiagReport, sendDiagReport, diagReportText } from '../diag/report.ts';
import { openHelp } from '../help/store.ts';
import { myReportsUnread, readAutoSend, writeAutoSend } from '../diag/helpDesk.ts';
import { outboxCount } from '../diag/reportQueue.ts';
import { AboutDetails } from './AboutDetails.tsx';
import { aggregateStatus, primaryBridge, getWtSession, connectionsVersion } from '../opbridge/store.ts';
import { wtSupported } from '../opbridge/wtTransport.ts';
import { describeSuiteCert, type SuiteCertStatus } from '../opbridge/suiteCertStatus.ts';
import {
  projects, activeProject, insertProject, setPanicMidiTrigger, PANIC_TRIGGER_ID,
} from '../layout/projectStore.ts';
import { MidiTriggerLearn } from './MidiTriggerLearn.tsx';
import { PanicButton } from './PanicButton.tsx';
import { panicInTopBar, setPanicInTopBar } from '../performance/panicPlacement.ts';
import {
  buildProjectBundle,
  buildAllBundle,
  parseBundle,
  importProjectBundle,
  importEverythingFromText,
  type ProjectImportResult,
} from '../bundle/serialize.ts';
import {
  downloadBlob,
  timestampForFilename,
  safeFilename,
  pickFile,
  readFileText,
} from '../bundle/download.ts';
import { matrixLibrary, setMatrixLibrary } from '../matrix/store.ts';
import { buildMatrixCsv, parseMatrixCsv, mergeMatrixLibraries } from '../matrix/export.ts';
import { ConfirmChoice } from './ConfirmChoice.tsx';
import { ToscImportPreview } from './ToscImportPreview.tsx';
import { thresholds, setThreshold, resetThresholds, type PreflightThresholds } from '../performance/preflightConfig.ts';
import type { MatrixLibrary } from '@opbuttons/schema';
import {
  projectSyncState,
  projectSyncLastError,
  projectSyncLastSyncedAt,
  projectSyncSkipped,
} from '../layout/projectSync.ts';

/** Editable pre-flight threshold fields (SD-13) — label + unit per key. */
const PREFLIGHT_FIELDS: { key: keyof PreflightThresholds; label: string; hint: string; unit: string }[] = [
  { key: 'rttWarnMs', label: 'Latency warning', hint: 'Warn when a round trip to your computer takes longer', unit: 'ms' },
  { key: 'rttFailMs', label: 'Latency failure', hint: 'Fail when it takes longer than this', unit: 'ms' },
  { key: 'latencySamples', label: 'Latency samples', hint: 'How many round trips each check times', unit: 'x' },
  { key: 'batteryWarnPct', label: 'Battery warning', hint: 'Warn below this charge', unit: '%' },
  { key: 'clockStaleFailMs', label: 'Clock stale', hint: "Fail when Ableton's timing has been silent this long", unit: 'ms' },
];

type ToastTone = 'ok' | 'err' | 'info';
interface Toast {
  tone: ToastTone;
  text: string;
}

export function SettingsPanel(props: {
  /** Only these sections (by data-section); all when absent. Navigate splits
   *  Settings into Look, Backup and Settings, as app-study.html does. */
  sections?: readonly string[];
} = {}) {
  const showSection = (k: string): boolean => !props.sections || props.sections.includes(k);
  const [toast, setToast] = createSignal<Toast | null>(null);

  // Black box verdict. Computed once when the panel opens rather than
  // reactively: it describes a run that has already ENDED, so there is nothing
  // for it to react to, and re-deriving it on every unrelated signal change
  // would parse the whole ring for no reason.
  const bbVerdict = createMemo(() => blackBoxVerdict());
  // The DURABLE half (2026-09-14). Separate from the verdict because they
  // answer different questions and one can be empty while the other is not:
  // the verdict describes the single run before this one, this describes
  // every drop this device has ever recorded. The verdict alone could not
  // answer "read the black box after the gig" once the app had been reopened.
  const bbHistory = createMemo(() => blackBoxHistorySummary());
  const [bbCopied, setBbCopied] = createSignal('');
  const [bbSending, setBbSending] = createSignal(false);
  // The ONE suite-wide setting for problem reports (OPApps, 2026-10-05).
  // null = not known (signed out / offline); the row then says so.
  const [autoSend, setAutoSend] = createSignal<boolean | null>(null);
  const [autoSendNote, setAutoSendNote] = createSignal('');
  onMount(() => { void readAutoSend().then((r) => setAutoSend(r.ok ? r.value : null)); });
  const toggleAutoSend = async (on: boolean) => {
    const r = await writeAutoSend(on);
    if (r.ok) { setAutoSend(r.value); setAutoSendNote(''); }
    else setAutoSendNote(r.reason === 'signin' ? 'Sign in to OverpoweredApps to change this.' : 'OverpoweredApps cannot be reached right now.');
  };
  const helpAt = (view?: Parameters<typeof openHelp>[0]) => { openHelp(view); };
  const [bbSent, setBbSent] = createSignal('');

  // Reactive clock for the project-sync age readout. Only runs while the
  // panel is mounted, so it costs nothing during a gig.
  const [nowTick, setNowTick] = createSignal(Date.now());
  onMount(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    onCleanup(() => clearInterval(id));
  });

  function flash(tone: ToastTone, text: string): void {
    setToast({ tone, text });
    window.setTimeout(() => {
      // Only clear if this is still the same toast (don't clobber a
      // later one that landed first).
      setToast((t) => (t?.text === text ? null : t));
    }, 6000);
  }

  // Land on a section when the caller asked for one (Navigate's Look and
  // Backup cards, and its search). Scrolled after first paint, then marked for
  // a moment so the eye finds it - landing mid-scroll with nothing to say
  // "this is what you asked for" reads as a random scroll position.
  onMount(() => {
    const want = settingsFocusSection();
    if (!want) return;
    clearSettingsFocusSection();
    requestAnimationFrame(() => {
      const el = document.querySelector(`.settings-pane [data-section="${want}"]`) as HTMLElement | null;
      if (!el) return;
      el.scrollIntoView({ block: 'start' });
      el.dataset.focus = 'true';
      window.setTimeout(() => { delete el.dataset.focus; }, 1600);
    });
  });

  // ─── Connect shortcut summary ──────────────────────────────────
  // One-line status echoing the header pill, so the shortcut is
  // glanceable ("Connected · Studio Mac") without duplicating the
  // pill's full count logic. Drives the launcher's meta line + dot.
  function connectSummary(): string {
    switch (aggregateStatus()) {
      case 'connected': {
        const p = primaryBridge();
        const name = p?.name || p?.bridge_instance_name || '';
        return name ? `Connected · ${name}` : 'Connected';
      }
      case 'partial':     return 'Some bridges offline';
      case 'unpaired':    return 'Needs pairing';
      case 'unreachable': return 'Offline';
      case 'none':        return 'Not set up';
      default:            return 'Not set up';
    }
  }
  function connectDot(): 'ok' | 'warn' | 'down' {
    switch (aggregateStatus()) {
      case 'connected':                return 'ok';
      case 'partial': case 'unpaired': return 'warn';
      default:                         return 'down';
    }
  }

  /** One line describing whether projects are actually reaching the
   *  bridge. Deliberately reports the LAST SUCCESSFUL TICK rather than
   *  just the current state enum: an error state that resolves itself
   *  reverts to 'idle', so a state-only readout would show a reassuring
   *  "Up to date" for a sync that has not completed in hours. The age is
   *  the part that catches a silent stall. */
  function projectSyncSummary(): string {
    const err = projectSyncLastError();
    if (err) return `Error: ${err}`;
    const at = projectSyncLastSyncedAt();
    if (at === null) {
      return projectSyncState() === 'idle'
        ? 'Not synced yet - needs a connected primary OPBridge'
        : 'Starting up';
    }
    // `nowTick()` and not a bare Date.now(): without a reactive clock
    // this line only re-renders when a tick SUCCEEDS, so a sync that
    // stopped (bridge disconnected, tab backgrounded) would sit showing
    // "last checked 2s ago" indefinitely - a readout that cannot report
    // the one condition it exists to report.
    const secs = Math.max(0, Math.round((nowTick() - at) / 1000));
    const ago = secs < 60 ? `${secs}s ago`
      : secs < 3600 ? `${Math.round(secs / 60)}m ago`
      : `${Math.round(secs / 3600)}h ago`;
    return `Up to date - last checked ${ago}`;
  }

  /** The offline path, in the operator's terms rather than the protocol's.
   *
   *  It reports what has ACTUALLY happened, never what is configured. A
   *  session that exists but has served nothing reads as "not carrying
   *  traffic", because "Ready" for a path that has never once worked is the
   *  precise shape of lie DEC-186 exists to prevent - and this is the one
   *  readout that only matters on a night when nothing else can be checked.
   *
   *  Reads `connectionsVersion()` first so it re-runs when a cert rotation
   *  swaps the session object underneath (a plain Map read tracks nothing). */
  function offlinePathSummary(): string {
    connectionsVersion();
    nowTick(); // keep the served count moving without a manual reopen
    const row = primaryBridge();
    const id = row?.id ?? null;
    if (!row || !id) return 'No default bridge';
    if (!wtSupported()) {
      // Naming the requirement matters: on an older iPad this is a permanent
      // property of the device, not a fault to go hunting for.
      return 'Not available on this device - needs iPadOS 26.4 or newer';
    }
    const wt = getWtSession(id);
    if (!wt) {
      return 'Not armed yet - connect to this bridge once while online';
    }
    if (wt.servedCount() > 0) {
      const where = wt.url().replace(/^https:\/\//, '');
      // The horizon, not the fact of being armed, is what decides whether a
      // device is safe to take to a venue - so it is stated rather than left
      // for the operator to infer. A single pin dies when the daemon re-mints
      // (~3 days before its own expiry); a published chain lasts months.
      const horizonS = row.wt_hashes_valid_until_unix_s ?? row.wt_expires_at_unix_s ?? null;
      if (!horizonS) return `Armed - ${where}, no internet needed`;
      const days = Math.floor((horizonS * 1000 - Date.now()) / 86_400_000);
      const pins = wt.pinCount();
      const lasts = days <= 0 ? 'expiring now'
        : days === 1 ? 'good for 1 more day offline'
        : `good for ${days} more days offline`;
      return `Armed - ${where}, ${lasts}${pins > 1 ? ` (${pins} pins)` : ''}`;
    }
    const err = wt.lastError();
    return err
      ? `Configured but not carrying traffic (${err})`
      : 'Configured, no traffic yet';
  }

  /** The ONLINE counterpart to the Offline-path row above - a SEPARATE row on
   *  purpose, because the two are different certificates with opposite failure
   *  modes. The offline path is a self-signed pin that needs no internet; this
   *  is the publicly-trusted suite cert, which needs internet to renew and only
   *  affects the online route. One merged "certificates" line would make each
   *  one's failure unreadable, which is the job here.
   *
   *  Decision lives in `describeSuiteCert` so all five states are reachable by
   *  a smoke; this wrapper is reactivity and nothing else. One memo, so the
   *  row's `data-state` and its text can never come from two reads of the
   *  clock. */
  const suiteCert = createMemo((): SuiteCertStatus => {
    connectionsVersion();
    const row = primaryBridge();
    if (!row) return { text: 'No default bridge', level: 'ok' };
    return describeSuiteCert({
      expiresAtUnixS: row.suite_cert_expires_at_unix_s,
      renewing: row.suite_cert_renewing,
      nowMs: nowTick(),
    });
  });

  // ─── Export ────────────────────────────────────────────────────

  function exportActiveProject(): void {
    const p = activeProject();
    if (!p) {
      flash('err', 'No active project to export.');
      return;
    }
    const bundle = buildProjectBundle(p);
    const json = JSON.stringify(bundle, null, 2);
    const name = `opbuttons-project-${safeFilename(p.name)}-${timestampForFilename()}.json`;
    downloadBlob(json, name, 'application/json');
    flash('ok', `Exported "${p.name}" - ${bundle.macros.macros.length} macro(s), ${bundle.sequencers.sequencers.length} sequencer(s) and ${bundle.timelines?.timelines.length ?? 0} timeline(s) bundled.`);
  }

  function exportAll(): void {
    if (projects().length === 0) {
      flash('err', 'No projects to export.');
      return;
    }
    const bundle = buildAllBundle(projects());
    const json = JSON.stringify(bundle, null, 2);
    const name = `opbuttons-all-${timestampForFilename()}.json`;
    downloadBlob(json, name, 'application/json');
    // The file carries the bridge pairings, bearers included (plan 3.2): say
    // so, because it can reach those OPBridges.
    flash('ok', `Exported everything - ${bundle.projects.length} project(s), ${bundle.timelines?.timelines.length ?? 0} timeline(s), ${bundle.rigStates?.banks.length ?? 0} Rig State(s), ${bundle.pairings?.length ?? 0} bridge pairing(s), libraries, matrix and settings. The file includes your bridge pairings: keep it private.`);
  }

  // ─── Import ────────────────────────────────────────────────────
  // Three flavors mirror Export: project bundle, matrix CSV, full
  // bundle. Each strictly checks the file's shape and refuses
  // mismatches with a clear error — no silent fallbacks.

  async function readPickedFile(accept: string): Promise<string | null> {
    const file = await pickFile(accept);
    if (!file) return null;
    try {
      return await readFileText(file);
    } catch (e) {
      flash('err', `Could not read file: ${(e as Error).message}`);
      return null;
    }
  }

  async function importProjectFromFile(): Promise<void> {
    const text = await readPickedFile('.json,application/json');
    if (text === null) return;
    const v = parseBundle(text);
    if (!v.ok) {
      flash('err', `Invalid bundle: ${v.reason}`);
      return;
    }
    if (v.bundle.kind !== 'project') {
      flash('err',
        `This file is "${v.bundle.kind}" - use Import everything for full bundles, or Export current project to make a project bundle.`,
      );
      return;
    }
    const r: ProjectImportResult = importProjectBundle(
      v.bundle,
      (p, makeActive) => insertProject(p, makeActive),
      true,
    );
    flash('ok',
      `Imported "${r.projectName}" as copy.`
      + ` Added ${r.macrosAdded} macro(s), ${r.sequencersAdded} sequencer(s) and ${r.timelinesAdded} timeline(s).`
      + ` Mapping fields preserved; matrix on this machine is independent - claim them in MATRIX.`,
    );
  }

  async function importEverythingFromFile(): Promise<void> {
    const text = await readPickedFile('.json,application/json');
    if (text === null) return;
    const r = importEverythingFromText(text);
    flash(r.ok ? 'ok' : 'err', r.message);
  }

  // Matrix CSV import is two-step: read the file, validate, then ask
  // the user whether to replace or merge. Pending state holds the
  // parsed library until the user makes the choice.
  const [pendingMatrix, setPendingMatrix] = createSignal<{
    library: MatrixLibrary;
    warningCount: number;
  } | null>(null);

  async function importMatrixFromFile(): Promise<void> {
    const text = await readPickedFile('.csv,text/csv,application/vnd.ms-excel');
    if (text === null) return;
    const parsed = parseMatrixCsv(text);
    if (!parsed.ok) {
      flash('err', `Invalid matrix CSV: ${parsed.reason}`);
      return;
    }
    if (parsed.library.slots.length === 0) {
      flash('err', 'CSV contained no valid slots.');
      return;
    }
    setPendingMatrix({
      library: parsed.library,
      warningCount: parsed.warnings.length,
    });
  }

  function applyMatrixImport(mode: 'replace' | 'merge'): void {
    const p = pendingMatrix();
    if (!p) return;
    if (mode === 'replace') {
      setMatrixLibrary(p.library);
      const warnNote = p.warningCount > 0 ? ` (${p.warningCount} warning(s))` : '';
      flash('ok',
        `Matrix replaced - ${p.library.devices.length} device(s),`
        + ` ${p.library.slots.length} slot(s)${warnNote}.`,
      );
    } else {
      const before = matrixLibrary();
      const merged = mergeMatrixLibraries(before, p.library);
      setMatrixLibrary(merged);
      flash('ok',
        `Matrix merged - now ${merged.devices.length} device(s),`
        + ` ${merged.slots.length} slot(s)`
        + ` (${merged.slots.length - before.slots.length} new).`,
      );
    }
    setPendingMatrix(null);
  }

  // ─── Export matrix ─────────────────────────────────────────────

  function exportMatrix(): void {
    const lib = matrixLibrary();
    if (lib.slots.length === 0) {
      flash('err', 'Matrix is empty - nothing to export.');
      return;
    }
    const csv = buildMatrixCsv(lib);
    downloadBlob(csv, `opbuttons-matrix-${timestampForFilename()}.csv`, 'text/csv');
    flash('ok', `Exported matrix - ${lib.devices.length} device(s), ${lib.slots.length} slot(s).`);
  }

  // Shared by the window and Navigate's Settings, Look and Backup panes.
  const body = () => (<>
            {/* ─── PANIC itself (2026-10-07, operator: "maybe that could be in
                 settings? default it to off"). The button always lives here;
                 the top bar shows it too only when this device says so. */}
            <Show when={showSection('panic')}>
            <section class="nv-ss" data-section="panic">
              <div class="nv-ss-h">
                <h2>Panic</h2>
                <p>Stops every stuck note and everything running, on every connected computer. Hold it to lock the rig silent; tap again to unlock.</p>
              </div>
              <div class="nv-row"><PanicButton /></div>
              <div class="nav-well">
                <label class="nav-lr nav-lr-button nv-check">
                  <span class="nav-lr-main">
                    <b>Also show Panic in the top bar</b>
                    <small>On this device, next to Help. Off, Panic is here, on a pedal (below) and in the search.</small>
                  </span>
                  <input type="checkbox" checked={panicInTopBar()} onChange={(e) => setPanicInTopBar(e.currentTarget.checked)} />
                </label>
              </div>
            </section>
            </Show>

            {/* ─── PANIC MIDI TRIGGER (DEC-154) - the one binding with no
                 canvas item to live on, so it gets a Settings section
                 instead of an Inspector block, same shape as Pre-flight
                 above (global, safety-adjacent, project-scoped). Reuses
                 MidiTriggerLearn as-is with the PANIC_TRIGGER_ID sentinel.
                 Pushes ONE `panic.fire` rule (ruleSync.ts::panicRule):
                 all-notes-off on every output port plus stop every engine,
                 server-side on the primary bridge, tab closed or not. The
                 header button additionally fans out to every connected
                 bridge; the copy below names that limit. */}
            <Show when={showSection('panic-midi-trigger')}>
            <section class="nv-ss" data-section="panic-midi-trigger">
              <div class="nv-ss-h">
                <h2>Panic from a pedal</h2>
                <p>Bind a pedal press to Panic: it stops every stuck note and everything running, even with this tab closed. It covers the computer the pedal is plugged into; the Panic button covers every computer.</p>
              </div>
              <div class="nav-well">
                <MidiTriggerLearn
                  variant="kit"
                  itemId={PANIC_TRIGGER_ID}
                  binding={activeProject()?.panicMidiTrigger}
                  path="a"
                  onSave={setPanicMidiTrigger}
                  onClear={() => setPanicMidiTrigger(undefined)}
                />
              </div>
            </section>
            </Show>

            {/* ─── CONNECT (shortcut) — the pairing surface itself moved
                 out into its own popup (opened from the header OPBridge
                 pill). This is a launcher into that popup + a glanceable
                 status echo, so Settings still reaches Connect in one tap. */}
            {/* "connection", not "connect": `[data-section="connect"]` is the
                Connect POPUP's own marker (ConnectPage), and a smoke asserts it
                is absent from Settings. */}
            <Show when={showSection('connection')}>
            <section class="nv-ss" data-section="connection">
              <div class="nv-ss-h"><h2>Connection</h2><p>The OPBridge computers this device talks to. Also opens from the OPBridge pill in the bar.</p></div>
              <div class="nav-well">
                {/* .settings-connect-shortcut is kept as the class a smoke
                    locates in strict mode; the kit classes carry the look. */}
                <button type="button" class="nav-lr nav-lr-button settings-connect-shortcut" data-state={connectDot()} onClick={openConnect}>
                  <span class="nv-dot" data-s={connectDot() === 'ok' ? 'ok' : connectDot() === 'warn' ? 'warn' : 'off'} aria-hidden="true" />
                  <span class="nav-lr-main"><b>Manage connections</b><small>{connectSummary()}</small></span>
                  <span class="nav-lr-chev" aria-hidden="true">›</span>
                </button>

                {/* Black box readout (2026-09-11). The recorder is worthless if
                    nobody can read it: `devHooks` is stripped from production,
                    and the iPad has no console. It leads with the VERDICT -
                    was the app alive or not - then the durable log over a
                    longer window, then where the last Send went (it STAYS: the
                    operator taps Send after a gig and walks away). */}
                <div class="nav-lr blackbox-row">
                  <span class="nav-lr-main">
                    <b>Last incident</b>
                    <small class="blackbox-verdict">{bbVerdict()}</small>
                    <small class="blackbox-verdict blackbox-history">{bbHistory()}</small>
                    <Show when={bbSent()}>
                      <small class="blackbox-verdict blackbox-history blackbox-sent">{bbSent()}</small>
                    </Show>
                  </span>
                  <span class="blackbox-actions">
                    {/* Send to my computer (2026-10-05, `diag/report.ts`): the
                        whole record onto every connected OPBridge's disk. */}
                    <button
                      type="button"
                      class="nv-b nv-b-sm"
                      data-testid="diag-send"
                      disabled={bbSending()}
                      onClick={() => {
                        setBbSending(true);
                        const report = buildDiagReport();
                        void sendDiagReport(report).then((r) => {
                          setBbSending(false);
                          const at = new Date().toLocaleTimeString();
                          if (r.tried === 0) {
                            setBbSent(`Not sent: no OPBridge connected. Use Copy instead.`);
                          } else if (r.sentTo.length === 0) {
                            setBbSent(`Not sent: ${r.failed.join(', ')} refused it. Use Copy instead.`);
                          } else {
                            setBbSent(`Sent to ${r.sentTo.join(' and ')} at ${at}.`
                              + (r.failed.length ? ` Failed on ${r.failed.join(', ')}.` : ''));
                          }
                        });
                      }}
                    >
                      {bbSending() ? 'Sending...' : 'Send to my computer'}
                    </button>
                    <button
                      type="button"
                      class="nv-b nv-b-sm"
                      onClick={() => {
                        const text = diagReportText(buildDiagReport());
                        navigator.clipboard?.writeText(text).then(
                          () => setBbCopied('Copied'),
                          () => setBbCopied('Copy failed - select the text'),
                        );
                        setTimeout(() => setBbCopied(''), 4000);
                      }}
                    >
                      {bbCopied() || 'Copy'}
                    </button>
                  </span>
                </div>
              </div>
            </section>
            </Show>

            {/* ─── DIAGNOSTICS — live traffic view (2026-08-11). Consumes
                 OPBridge's traffic-monitor rings (Session 111, live since
                 v1.13.26) — REST backfill + WS push, connected only while
                 the panel is open (opbridge/trafficMonitor.ts). */}
            {/* Help and problem reports (2026-10-05). The answers are bundled,
                so Help opens offline; reports wait in an outbox until they
                can be sent (diag/reportQueue.ts). */}
            <Show when={showSection('help')}>
            <section class="nv-ss" data-section="help">
              <div class="nv-ss-h"><h2>Help</h2></div>
              <div class="nav-well">
                <button type="button" class="nav-lr nav-lr-button" onClick={() => helpAt()}>
                  <span class="nav-lr-main"><b>Help</b><small>Answers to "how do I...", and what's new</small></span>
                  <span class="nav-lr-chev" aria-hidden="true">›</span>
                </button>
                <button type="button" class="nav-lr nav-lr-button" onClick={() => helpAt({ v: 'report', type: 'bug' })}>
                  <span class="nav-lr-main"><b>Report a problem</b><small>{outboxCount() > 0 ? `${outboxCount()} waiting to send` : 'Or suggest an idea, or ask a question, from Help'}</small></span>
                  <span class="nav-lr-chev" aria-hidden="true">›</span>
                </button>
                <button type="button" class="nav-lr nav-lr-button" onClick={() => helpAt({ v: 'reports' })}>
                  <span class="nav-lr-main"><b>My reports</b><small>{myReportsUnread() > 0 ? `${myReportsUnread()} new` : 'Replies and what happened to what you sent'}</small></span>
                  <span class="nav-lr-chev" aria-hidden="true">›</span>
                </button>
                <div class="nav-lr">
                  <span class="nav-lr-main">
                    <b>Send problem reports automatically</b>
                    <small>
                      {autoSend() === null
                        ? 'Sign in and go online to see this setting.'
                        : 'One setting for every OverpoweredApps app. Off: you are asked first.'}
                      {autoSendNote() ? ` ${autoSendNote()}` : ''}
                    </small>
                  </span>
                  <span class="nv-seg">
                    <button type="button" aria-pressed={autoSend() === false} disabled={autoSend() === null} onClick={() => void toggleAutoSend(false)}>Ask me</button>
                    <button type="button" aria-pressed={autoSend() === true} disabled={autoSend() === null} onClick={() => void toggleAutoSend(true)}>Always send</button>
                  </span>
                </div>
              </div>
            </section>
            </Show>

            <Show when={showSection('diagnostics')}>
            <section class="nv-ss" data-section="diagnostics">
              <div class="nv-ss-h"><h2>Diagnostics</h2><p>What OPBridge receives, and how this device syncs and reaches it.</p></div>
              <div class="nav-well">
                <button type="button" class="nav-lr nav-lr-button settings-traffic-monitor-shortcut" onClick={openTrafficMonitor}>
                  <span class="nav-lr-main"><b>Traffic Monitor</b><small>Live inbound MIDI and OSC</small></span>
                  <span class="nav-lr-chev" aria-hidden="true">›</span>
                </button>
                {/* Project sync readout (2026-08-31): read-only on purpose - a
                    tick already runs every 5 s, and a "sync now" would mislead. */}
                <div class="nav-lr settings-project-sync-status">
                  <span class="nav-lr-main">
                    <b>Project sync</b>
                    <small>{projectSyncSummary()}</small>
                    <Show when={projectSyncSkipped().length > 0}>
                      <small class="settings-project-sync-skipped">
                        {projectSyncSkipped().length === 1
                          ? `1 project on the bridge can't be opened here (${projectSyncSkipped()[0]!.reason}) - left as it is.`
                          : `${projectSyncSkipped().length} projects on the bridge can't be opened here - left as they are.`}
                      </small>
                    </Show>
                  </span>
                </div>
                {/* Offline path readout (2026-08-31): whether the rig would
                    survive losing internet, said at the desk. */}
                <div class="nav-lr settings-offline-path-status">
                  <span class="nav-lr-main"><b>Offline path</b><small>{offlinePathSummary()}</small></span>
                </div>
                {/* Suite certificate (2026-09-01): the warning OPBridge sends
                    about a cert that will strand the online path. */}
                <div class="nav-lr settings-suite-cert-status" data-state={suiteCert().level}>
                  <span class="nav-lr-main"><b>Suite certificate</b><small>{suiteCert().text}</small></span>
                </div>
              </div>
            </section>
            </Show>

            {/* ─── INTERFACE — per-device chrome preferences. Bar position
                 flips the single chrome bar (metronome/BPM, project, pages,
                 status, settings) between the top and bottom of the screen. */}
            <Show when={showSection('interface')}>
            <section class="nv-ss" data-section="interface">
              <div class="nv-ss-h"><h2>Interface</h2></div>
              <div class="nav-well">
                <div class="nav-lr">
                  <span class="nav-lr-main"><b>Bar position</b><small>Where the bar sits on the stage</small></span>
                  <span class="nv-seg">
                    <button type="button" aria-pressed={barPosition() === 'top'} onClick={() => setBarPosition('top')}>Top</button>
                    <button type="button" aria-pressed={barPosition() === 'bottom'} onClick={() => setBarPosition('bottom')}>Bottom</button>
                  </span>
                </div>
                <div class="nav-lr">
                  <span class="nav-lr-main">
                    <b>Controls fire while you edit</b>
                    <small>Off: a tap only selects. On: it also sends; with a mouse, hover a control about 2 s to drag it.</small>
                  </span>
                  <span class="nv-seg">
                    <button type="button" aria-pressed={!editTriggersEnabled()} onClick={() => setEditTriggersEnabled(false)}>Off</button>
                    <button type="button" aria-pressed={editTriggersEnabled()} onClick={() => setEditTriggersEnabled(true)}>On</button>
                  </span>
                </div>
                {/* DEC-268: on a desktop Navigate stays beside the canvas;
                    the iPad and the phone keep the full-screen launcher. */}
                <div class="nav-lr" data-setting="dock">
                  <span class="nav-lr-main">
                    <b>Navigate beside the canvas</b>
                    <small>On a computer: the list stays on the left and the canvas is its first page. The logo or \ hides it.</small>
                  </span>
                  <span class="nv-seg">
                    <button type="button" aria-pressed={!dockPref()} onClick={() => setDockPref(false)}>Off</button>
                    <button type="button" aria-pressed={dockPref()} onClick={() => setDockPref(true)}>On</button>
                  </span>
                </div>
                {/* 2026-11-03, operator: reload "same as you left" (the default) or onto the canvas (navigate/resume.ts). */}
                <div class="nav-lr" data-setting="reload-opens">
                  <span class="nav-lr-main">
                    <b>After a reload</b>
                    <small>Where you left off: the timeline or section you had open. The canvas: always the stage.</small>
                  </span>
                  <span class="nv-seg">
                    <button type="button" aria-pressed={reloadOpens() === 'left'} onClick={() => setReloadOpens('left')}>Where you left off</button>
                    <button type="button" aria-pressed={reloadOpens() === 'canvas'} onClick={() => setReloadOpens('canvas')}>The canvas</button>
                  </span>
                </div>
                <div class="nav-lr" data-setting="snap">
                  <span class="nav-lr-main">
                    <b>Snap while moving</b>
                    <small>Controls line up with each other and keep an even gap. Hold Alt to place freely.</small>
                  </span>
                  <span class="nv-seg">
                    <button type="button" aria-pressed={!snapEnabled()} onClick={() => setSnapEnabled(false)}>Off</button>
                    <button type="button" aria-pressed={snapEnabled()} onClick={() => setSnapEnabled(true)}>On</button>
                  </span>
                </div>
                <label class="nav-lr" data-setting="control-gap">
                  <span class="nav-lr-main">
                    <b>Space between controls</b>
                    <small>What a moved control keeps from its neighbours, and how far apart new ones are placed. 0 puts them edge to edge.</small>
                  </span>
                  <span class="nv-fld nv-fld-num">
                    <input
                      type="number" min="0" max={CONTROL_GAP_MAX} step="1"
                      value={controlGap()}
                      aria-label="Space between controls"
                      onChange={(e) => setControlGap(parseFloat(e.currentTarget.value))}
                    />
                    <span class="nv-unit">px</span>
                  </span>
                </label>
                {/* Screen dim (was "stage dim", SD-4): an ordinary per-device
                    setting since DEC-216, darker than the iPad's own minimum. */}
                <label class="nav-lr settings-dim">
                  <span class="nav-lr-main"><b>Screen dim</b><small>Darker than the iPad's lowest brightness, for dark stages</small></span>
                  <span class="nv-range">
                    <input
                      type="range"
                      class="settings-dim-slider"
                      min={STAGE_DIM_MIN}
                      max={STAGE_DIM_MAX}
                      step="0.05"
                      value={stageDim()}
                      onInput={(e) => setStageDim(parseFloat(e.currentTarget.value))}
                      aria-label="Screen dim"
                    />
                    <span class="settings-dim-value">{Math.round(stageDim() * 100)}%</span>
                  </span>
                </label>
              </div>
            </section>
            </Show>

            {/* ─── PRE-FLIGHT (SD-13) — on-demand run + adjustable thresholds.
                 Thresholds are machine truth (this iPad's venue WiFi), saved
                 per device, never synced. */}
            <Show when={showSection('preflight')}>
            <section class="nv-ss" data-section="preflight">
              <div class="nv-ss-h">
                <h2>Thresholds</h2>
                <p>When a check above warns or fails. Saved on this device only: a venue's WiFi belongs to the room and this iPad.</p>
              </div>
              <div class="nav-well">
                <For each={PREFLIGHT_FIELDS}>
                  {(f) => (
                    <label class="nav-lr">
                      <span class="nav-lr-main"><b>{f.label}</b><small>{f.hint}</small></span>
                      <span class="nv-fld nv-fld-num">
                        <input
                          type="number" min="1"
                          value={thresholds()[f.key]}
                          aria-label={f.label}
                          onChange={(e) => setThreshold(f.key, parseFloat(e.currentTarget.value))}
                        />
                        <span class="nv-unit">{f.unit}</span>
                      </span>
                    </label>
                  )}
                </For>
              </div>
              <div class="nv-row">
                <button type="button" class="nv-b nv-b-q" onClick={resetThresholds}>Reset to the defaults</button>
              </div>
            </section>
            </Show>

            {/* ─── EXPORT — three buttons of graduated weight.
                 "Export current project" + "Export matrix" are the
                 everyday actions, equal size, cyan. "Export
                 everything" is the heavyweight all-in-one - bigger,
                 amber, so the visual hierarchy says "this is the
                 big one." */}
            <Show when={showSection('export')}>
            <section class="nv-ss" data-section="export">
              <div class="nv-ss-h"><h2>Export</h2><p>Save your work to a file. Projects carry their macros and sequencers; the matrix saves as a spreadsheet-friendly CSV.</p></div>
              <div class="nv-grid nv-g3">
                {/* Everything first: it is the one a backup means. */}
                <button type="button" class="nv-qa" data-hero onClick={exportAll} disabled={projects().length === 0}>
                  <span class="nv-qa-lead"><svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 4l5.5-2.5L13.5 4v8l-5.5 2.5L2.5 12z" /><path d="M2.5 4L8 6.5l5.5-2.5" /><path d="M8 6.5v8" /></g></svg></span>
                  <b>Export everything</b>
                  <small>{projects().length} {projects().length === 1 ? 'project' : 'projects'}, timelines, Rig States, libraries, the matrix, pairings and settings</small>
                </button>
                <button type="button" class="nv-qa" onClick={exportActiveProject} disabled={activeProject() === null}>
                  <b>Export this project</b>
                  <small>{activeProject()?.name ?? 'No project open'}</small>
                </button>
                <button type="button" class="nv-qa" onClick={exportMatrix} disabled={matrixLibrary().slots.length === 0}>
                  <b>Export the matrix</b>
                  <small>{matrixLibrary().devices.length} {matrixLibrary().devices.length === 1 ? 'device' : 'devices'}, as CSV</small>
                </button>
              </div>
            </section>
            </Show>

            {/* ─── IMPORT — mirrors Export structure: two equal cyan
                 buttons (project + matrix) above one bigger amber
                 button (everything). Each strictly checks the file's
                 shape and rejects mismatches. Matrix import asks
                 replace-vs-merge before applying. */}
            <Show when={showSection('import')}>
            <section class="nv-ss" data-section="import">
              <div class="nv-ss-h"><h2>Import</h2><p>Bring work in from a file. A project lands as a copy. Everything puts a backup back: anything already here is kept unless the file's copy is newer.</p></div>
              <div class="nv-grid nv-g3">
                <button type="button" class="nv-qa" data-hero onClick={() => { void importEverythingFromFile(); }}>
                  <span class="nv-qa-lead"><svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 4l5.5-2.5L13.5 4v8l-5.5 2.5L2.5 12z" /><path d="M2.5 4L8 6.5l5.5-2.5" /><path d="M8 6.5v8" /></g></svg></span>
                  <b>Import everything</b>
                  <small>A full backup (.json): projects, timelines, pairings and settings</small>
                </button>
                <button type="button" class="nv-qa" onClick={() => { void importProjectFromFile(); }}>
                  <b>Import a project</b>
                  <small>One project (.json)</small>
                </button>
                <button type="button" class="nv-qa" onClick={() => { void importMatrixFromFile(); }}>
                  <b>Import a matrix</b>
                  <small>A CSV; you choose replace or merge</small>
                </button>
              </div>
            </section>
            </Show>

            {/* ─── TOUCHOSC IMPORT PREVIEW — step 1 of the eventual
                 importer. Pick a .tosc file → the PWA inflates + parses
                 it in-browser (tosc/parse.ts; was the legacy bridge
                 until 2026-07-27) → we render counts + flags + routing
                 tables. No items get added to any project yet; the
                 import button lands as a later turn. */}
            <Show when={showSection('import')}><ToscImportPreview flash={flash} /></Show>

            {/* About (2026-10-05): the build and, on demand, this device's frame
                numbers and frame report. Moved here from Navigate's footer,
                as app-study.html has it. Last, like an About page anywhere. */}
            <Show when={showSection('about')}>
            <section class="nv-ss" data-section="about">
              <div class="nv-ss-h"><h2>About</h2></div>
              <AboutDetails />
            </section>
            </Show>

            <Show when={pendingMatrix() !== null}>
              {(() => {
                const p = pendingMatrix();
                if (!p) return null;
                const incoming = p.library;
                const current = matrixLibrary();
                const message =
                  `Incoming: ${incoming.devices.length} device(s), ${incoming.slots.length} slot(s).`
                  + ` Current: ${current.devices.length} device(s), ${current.slots.length} slot(s).`
                  + (p.warningCount > 0 ? ` (${p.warningCount} parser warning(s).)` : '');
                return (
                  <ConfirmChoice
                    title="Import matrix"
                    message={message}
                    options={[
                      {
                        key: 'replace',
                        label: 'Replace current matrix',
                        hint: 'Overwrites everything in MATRIX with the file.',
                        tone: 'danger',
                      },
                      {
                        key: 'merge',
                        label: 'Merge into current',
                        hint: 'Adds new devices + slots; existing claims kept.',
                        tone: 'primary',
                      },
                    ]}
                    onChoose={(key) => applyMatrixImport(key as 'replace' | 'merge')}
                    onCancel={() => setPendingMatrix(null)}
                  />
                );
              })()}
            </Show>

  </>);
  return (
    <div class="nav-pane-body settings-body settings-pane" role="region" aria-label="Settings">
      {body()}
        <Show when={toast() !== null}>
          <div class="nv-note nv-toast" data-tone={toast()!.tone === 'err' ? 'warn' : 'info'}>
            <span>{toast()!.text}</span>
          </div>
        </Show>
    </div>
  );
}

